/*----------------------------------
- TLD PAGES LINKS
----------------------------------*/

/**
 * @docs docs/features/tld-intelligence
 * @rule Where a TLD named on a public page links (D31): its insight page for a public-set TLD,
 *       once published, else its dates on the agenda. Formats and display rows live in the shared
 *       TLD page (`@uniquedomains/components/tlds/page/pageModel`, `contracts/tlds/format`), read
 *       by the page and its Markdown mirror alike; both link the same published siblings here.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { tldInsightPath } from '@uniquedomains/components/contracts/tlds/public';
import { AGENDA_PATH } from '@uniquedomains/core/domains/tlds';

/*----------------------------------
- LINKS
----------------------------------*/

/**
 * The published insight pages a page may link, or null when links are not gated (every profile
 * but prod): the render context's `renderedDomainResourcePaths` (the warmup's linkable pages),
 * read by the pages through `usePublishedTldPaths` and by the full mirror's route.
 */
export const resolvePublishedTldPaths = ({
    enforceDomainPublicationLinks,
    renderedDomainResourcePaths,
}: {
    enforceDomainPublicationLinks: boolean;
    renderedDomainResourcePaths: readonly string[];
}): ReadonlySet<string> | null => (enforceDomainPublicationLinks ? new Set(renderedDomainResourcePaths) : null);

/**
 * The insight page href of a TLD, or null when this page must not link it: in production a link
 * waits until the warmup has published the page (`enforceDomainPublicationLinks`), like every
 * other link to a published listing, so a static page never points at a page that answers 503.
 */
export const resolvePublishedTldHref = (
    slug: string,
    publishedPaths: ReadonlySet<string> | null,
): string | null => {
    const href = tldInsightPath(slug);
    return !publishedPaths || publishedPaths.has(href) ? href : null;
};

/**
 * "Other TLD pages": the rest of the public set in Product's order, published pages only and
 * never the page itself, for the page's card and its mirror alike.
 */
export const listTldSiblingLinks = (
    slug: string,
    relatedSlugs: readonly string[],
    publishedPaths: ReadonlySet<string> | null,
): Array<{ slug: string; href: string }> =>
    relatedSlugs.flatMap((relatedSlug) => {
        const href = relatedSlug === slug ? null : resolvePublishedTldHref(relatedSlug, publishedPaths);
        return href ? [{ slug: relatedSlug, href }] : [];
    });

/**
 * A TLD's dates on the agenda, for a TLD with no page of its own (D31). Written as the agenda's
 * own normalized URL (current month and list view omitted, see `buildAgendaHref`), built here
 * rather than through the agenda codec, which would ship the country table with this page.
 */
export const buildTldAgendaHref = (slug: string): string => `${AGENDA_PATH}?tld=${encodeURIComponent(slug)}`;

/**
 * Where a TLD named on a public page links (D31): its insight page for a public-set TLD, once
 * published, else its dates on the agenda.
 */
export const resolveTldHref = (
    slug: string,
    publicSlugs: ReadonlySet<string>,
    publishedPaths: ReadonlySet<string> | null,
): string => (publicSlugs.has(slug) ? resolvePublishedTldHref(slug, publishedPaths) : null) ?? buildTldAgendaHref(slug);
